import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import Modal from '@medflex/components/stories/Modal';
import InputField from '@medflex/components/stories/InputField';
import ContactList from '@medflex/components/stories/ContactList';
import IconSearch from '@medflex/components/stories/icons/Search';
import IconClose from '@medflex/components/stories/icons/Close';
import Button from '@medflex/components/stories/Button';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import useAddressBookContacts from '../hooks/useAddressBookContacts';
import useContactConnector from '../hooks/useContactConnector';
import useModal from '../hooks/useModal';
import useSharePatientContact from '../hooks/useShareContact';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';

const termMatched = (contact, searchTerm) =>
  contact.user.fullNameWithTitle.toLowerCase().includes(searchTerm.toLowerCase().trim());

const DeselectContactActionMenu = ({ handleClick }) => (
  <IconClose
    className="cursor-pointer text-slate-400 hover:text-slate-500"
    size="5"
    onClick={handleClick}
  />
);

DeselectContactActionMenu.propTypes = {
  handleClick: PropTypes.func,
};

const SharePatientContactModule = () => {
  const { close } = useModal();
  const { t } = useTranslation('physician');
  const addressContacts = useAddressBookContacts();
  const { share, isLoading, errors } = useSharePatientContact();
  const { recipient, contactUserId, filter = 'patients' } = useLocationSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [checkIfConnected, setCheckIfConnected] = useState([]);
  const { isConnected } = useContactConnector(checkIfConnected);
  const inputFieldRef = useRef();
  const isSelected = Boolean(selectedContact);
  const contacts =
    filter === 'physicians'
      ? [...addressContacts.pairedPhysicians, ...addressContacts.orgaPhysicians]
      : [...addressContacts.pairedPatients];
  const selectedContactUserId = get(selectedContact, 'user.id');
  const isSharedOverDirectMessage = filter === 'patients' && recipient;
  const recipientId = isSharedOverDirectMessage ? recipient : selectedContactUserId;
  const patientUserId = isSharedOverDirectMessage ? selectedContactUserId : contactUserId;

  const handleClose = () =>
    close({
      modal: 'sharePatientContact',
      recipient,
      contactUserId,
      filter,
    });

  useEffect(() => {
    if (!isLoading && isSubmitted) {
      if (errors && errors.share_patient_contact) {
        showAlertToast(
          t('modals.share_patient_contact.toast.alert.title'),
          t('modals.share_patient_contact.toast.alert.description'),
          {
            toastId: 'sharePatientFailed',
          },
        );
        setIsSubmitted(false);
        return;
      }

      if (!isSharedOverDirectMessage) {
        showSuccessToast(t('modals.share_patient_contact.toast.success'), null, {
          toastId: 'sharePatientSuccess',
        });
      }

      trackEvent({
        zone: 'SharePatientContactModal',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.FORM,
        actionText: 'send',
      });
      handleClose();
    }
  }, [errors, isLoading, isSubmitted]);

  useEffect(() => {
    if (isConnected) {
      setIsSubmitted(true);
      share({ id: patientUserId }, recipientId);
    }
  }, [isConnected]);

  return (
    <Modal
      showCloseButton
      title={t('modals.share_patient_contact.headline')}
      onClickCloseButton={() => handleClose()}
    >
      <div data-component="SharePatientContactModal" className="flex flex-col">
        <div className="mb-4">{t('modals.share_patient_contact.patient_agreement_note')}</div>
        <div className="mb-4 font-semibold">
          {t(`modals.share_patient_contact.${filter}.input.label`)}
        </div>
        {selectedContact ? (
          <div className="mb-4">
            <ContactList.Item
              key={`selected-${selectedContact.id}`}
              avatarSrc={selectedContact.user.avatar}
              avatarInitials={selectedContact.user.initials}
              avatarSize="8"
              showFullBorder
              title={selectedContact.user.fullNameWithTitle}
              selected={Boolean(selectedContact)}
              subtitle={selectedContact.user.subtitle}
              contactUserId={selectedContact.id}
              // eslint-disable-next-line react/no-unstable-nested-components,
              ActionMenu={props => (
                <DeselectContactActionMenu
                  {...props}
                  handleClick={() => setSelectedContact(null)}
                />
              )}
              enabled
            />
          </div>
        ) : (
          <>
            <InputField
              placeholder={t(`modals.share_patient_contact.${filter}.input.placeholder`)}
              className="mb-4"
              innerRef={inputFieldRef}
              onChange={event => {
                setSearchTerm(event.target.value);
              }}
              value={searchTerm}
              type="search"
              afterInput={<IconSearch size="5" className="mr-3 text-slate-400" />}
            />
            <div className="flex overflow-y-auto lg:max-h-124 mb-4">
              <ContactList className="w-full">
                {contacts.map(contact => {
                  if (
                    contact.disabled ||
                    contact.mainContactId ||
                    !termMatched(contact, searchTerm)
                  ) {
                    return undefined;
                  }

                  return (
                    <ContactList.Item
                      key={contact.id}
                      avatarSrc={contact.user.avatar}
                      avatarInitials={contact.user.initials}
                      avatarSize="8"
                      showBorder={false}
                      title={contact.user.fullNameWithTitle}
                      subtitle={contact.user.subtitle}
                      enabled
                      onClick={() => setSelectedContact(contact)}
                    />
                  );
                })}
              </ContactList>
            </div>
          </>
        )}
        <div className="mb-6 text-slate-600 text-sm">{t('modals.share_patient_contact.hint')}</div>
        <div className="w-full flex justify-end">
          <Button
            className="self-end"
            type="submit"
            isDisabled={!isSelected}
            isLoading={isLoading}
            onClick={() => {
              setCheckIfConnected([recipientId]);
            }}
          >
            {t('modals.share_patient_contact.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SharePatientContactModule;
